import { useEffect, useState } from "react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-[#F5F3EF] text-[#5C635A] border-[#E5E2DC]",
  confirmed: "bg-[#1C3326]/10 text-[#1C3326] border-[#1C3326]/30",
  completed: "bg-[#C26D5C]/10 text-[#C26D5C] border-[#C26D5C]/40",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 sm:px-8 md:px-12 py-16" data-testid="admin-dashboard">
        <div className="mb-10">
          <p className="tag-line text-[#C26D5C] mb-3">Admin · Operations</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight">Incoming bookings</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            ["Total", stats.total],
            ["Pending", stats.pending],
            ["Confirmed", stats.confirmed],
            ["Completed", stats.completed],
          ].map(([label, val]) => (
            <div key={label} className="bg-white border border-[#E5E2DC] p-6" data-testid={`stat-${label.toLowerCase()}`}>
              <p className="tag-line text-[#5C635A]">{label}</p>
              <p className="font-serif text-4xl text-[#1A201C] mt-1">{val}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl text-[#1A201C]">All bookings</h2>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 rounded-sm border-[#E5E2DC] bg-white" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-[#E5E2DC] overflow-x-auto">
          {loading ? (
            <p className="p-8 text-[#5C635A]">Loading...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E2DC]">
                  <TableHead className="text-[#1A201C] tag-line">Date / Time</TableHead>
                  <TableHead className="text-[#1A201C] tag-line">Customer</TableHead>
                  <TableHead className="text-[#1A201C] tag-line">Address</TableHead>
                  <TableHead className="text-[#1A201C] tag-line">Service</TableHead>
                  <TableHead className="text-[#1A201C] tag-line">Status</TableHead>
                  <TableHead className="text-[#1A201C] tag-line">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#5C635A]">No bookings match this filter</TableCell></TableRow>
                ) : filtered.map(b => (
                  <TableRow key={b.booking_id} className="border-[#E5E2DC]" data-testid={`admin-row-${b.booking_id}`}>
                    <TableCell className="text-[#1A201C]">
                      <div className="font-medium">{b.preferred_date}</div>
                      <div className="text-xs text-[#5C635A]">{b.preferred_time}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#1A201C]">{b.name}</div>
                      <div className="text-xs text-[#5C635A]">{b.email}</div>
                      <div className="text-xs text-[#5C635A]">{b.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm text-[#5C635A] max-w-[200px]">{b.apartment_address}</TableCell>
                    <TableCell className="text-sm text-[#5C635A]">{b.service_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-sm capitalize ${STATUS_STYLES[b.status]}`}>{b.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => updateStatus(b.booking_id, v)}>
                        <SelectTrigger className="w-36 rounded-sm border-[#E5E2DC]" data-testid={`set-status-${b.booking_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
